import { FC } from "react";

interface Props {
  items: Array<string> | Array<{ dayIndex: number; slots: string[] }>;
  fieldPath: string;
}

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const slotToTime = (slot: number): string => {
  const hours = Math.floor(slot / 2);
  const minutes = (slot % 2) * 30;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

const groupSlotsIntoRanges = (slots: number[]): string[] => {
  if (slots.length === 0) return [];

  const sortedSlots = [...slots].sort((a, b) => a - b);
  const ranges: string[] = [];
  let rangeStart = sortedSlots[0];
  let rangeEnd = sortedSlots[0];

  for (let i = 1; i < sortedSlots.length; i++) {
    if (sortedSlots[i] === rangeEnd + 1) {
      rangeEnd = sortedSlots[i];
    } else {
      const startTime = slotToTime(rangeStart);
      const endTime = slotToTime(rangeEnd + 1);
      ranges.push(`${startTime} - ${endTime}`);
      rangeStart = sortedSlots[i];
      rangeEnd = sortedSlots[i];
    }
  }

  const startTime = slotToTime(rangeStart);
  const endTime = slotToTime(rangeEnd + 1);
  ranges.push(`${startTime} - ${endTime}`);

  return ranges;
};

const extractDayOfWeek = (fieldPath: string): number | null => {
  // Match pattern: /dayparting/{number}/halfHourOfDay
  const match = fieldPath.match(/\/dayparting\/(\d+)\//);
  if (!match) return null;

  const dayIndex = parseInt(match[1], 10);

  return dayIndex + 1;
};

const DaypartingField: FC<Props> = ({ items, fieldPath }) => {
  if (
    items.length > 0 &&
    typeof items[0] === "object" &&
    items[0] !== null &&
    "dayIndex" in items[0] &&
    "slots" in items[0]
  ) {
    const structuredItems = items as unknown as Array<{
      dayIndex: number;
      slots: string[];
    }>;
    return (
      <div>
        {structuredItems.map((dayData, idx) => {
          const slots = dayData.slots
            .map((item) => parseInt(String(item), 10))
            .filter((num) => !isNaN(num) && num >= 0 && num < 48);

          if (slots.length === 0) return null;

          const dayOfWeek = dayData.dayIndex + 1; // dayIndex is 0-based, dayOfWeek is 1-based
          const dayName =
            dayOfWeek >= 1 && dayOfWeek <= 7
              ? DAY_NAMES[dayOfWeek - 1]
              : "Unknown Day";

          const allSlots = new Set(slots);
          const isFullDay =
            allSlots.size === 48 &&
            slots.length === 48 &&
            Array.from({ length: 48 }, (_, i) => i).every((slot) =>
              allSlots.has(slot)
            );

          if (isFullDay) {
            return <div key={idx}>{dayName} (full day)</div>;
          }

          const timeRanges = groupSlotsIntoRanges(slots);

          return (
            <div key={idx}>
              {timeRanges.map((range, rangeIdx) => (
                <div key={rangeIdx}>
                  {range} {dayName}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  const slots = items
    .map((item) => parseInt(String(item), 10))
    .filter((num) => !isNaN(num) && num >= 0 && num < 48);

  if (slots.length === 0) {
    return <div>{items.join(", ")}</div>;
  }

  const dayOfWeek = extractDayOfWeek(fieldPath);
  const dayName =
    dayOfWeek && dayOfWeek >= 1 && dayOfWeek <= 7
      ? DAY_NAMES[dayOfWeek - 1]
      : "Unknown Day";

  const allSlots = new Set(slots);
  const isFullDay =
    allSlots.size === 48 &&
    slots.length === 48 &&
    Array.from({ length: 48 }, (_, i) => i).every((slot) => allSlots.has(slot));

  if (isFullDay) {
    return <div>{dayName} (full day)</div>;
  }

  const timeRanges = groupSlotsIntoRanges(slots);

  return (
    <div>
      {timeRanges.map((range, index) => (
        <div key={index}>
          {range} {dayName}
        </div>
      ))}
    </div>
  );
};

export default DaypartingField;
